'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["customFonts", "defaultFonts", "enableParsedFonts", "fetchBrandingCustomFonts", "addBrandingCustomFonts", "isUngatedForBrandingFonts", "parsedFonts", "parsedFontText", "unsupportedFonts", "value", "webfonts"];
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import UIButton from 'UIComponents/button/UIButton';
import GroupedFontSelectBody from './GroupedFontSelectBody';
import { useState, useEffect } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = suffix => `RichTextLib.formatting.FontSelect.${suffix}`;
function GroupedFontSelect(props) {
  const {
      customFonts,
      defaultFonts,
      enableParsedFonts,
      fetchBrandingCustomFonts,
      addBrandingCustomFonts,
      isUngatedForBrandingFonts,
      parsedFonts,
      parsedFontText,
      unsupportedFonts,
      value,
      webfonts
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [formattedBrandingFonts, setFormattedBrandingFonts] = useState([]);
  useEffect(() => {
    if (isUngatedForBrandingFonts && fetchBrandingCustomFonts && addBrandingCustomFonts && !formattedBrandingFonts.length) {
      fetchBrandingCustomFonts().then(fonts => {
        const formattedFonts = fonts.map(font => {
          return {
            family: font.name,
            fallback: font.fallback,
            text: font.name,
            value: font.name,
            variants: font.variants.map(variant => variant.name),
            svgUrls: font.variants.reduce((hash, variant) => {
              hash[variant.name] = variant.svgUrl;
              return hash;
            }, {}),
            styles: font.variants.reduce((hash, variant) => {
              let variantStyles = {};
              try {
                variantStyles = JSON.parse(variant.style);
              } catch (e) {
                console.error(e);
              }
              hash[variant.name] = variantStyles;
              return hash;
            }, {})
          };
        });
        setFormattedBrandingFonts(formattedFonts);
        addBrandingCustomFonts(fonts);
      }).catch(error => {
        console.error(error);
        setFormattedBrandingFonts([]);
        addBrandingCustomFonts([]);
      });
    }
    // we just want this to run on init, not everytime tinymce renders, so removing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isUnsupportedFont = unsupportedFonts.includes(value);
  const options = useMemo(() => {
    const webFontOptions = webfonts.length ? [{
      text: I18n.text(i18nKey('webfontTitle')),
      help: I18n.text(i18nKey('webfontTooltip')),
      options: webfonts
    }] : [];
    const parsedFontOptions = parsedFonts.length ? [{
      text: parsedFontText.text,
      help: parsedFontText.help,
      options: parsedFonts.map(({
        text,
        value: font_value
      }) => ({
        text,
        value: font_value,
        disabled: !enableParsedFonts
      }))
    }] : [];
    const combinedCustomFonts = [...customFonts, ...(isUngatedForBrandingFonts ? formattedBrandingFonts : [])];
    const parsedCustomFontOptions = combinedCustomFonts.length ? [{
      text: I18n.text(i18nKey('customFontsTitle')),
      options: combinedCustomFonts.map(fontOption => ({
        family: fontOption.family,
        fallback: fontOption.fallback,
        text: fontOption.family,
        value: fontOption.value,
        variants: fontOption.variants,
        svgUrls: fontOption.svgUrls,
        styles: fontOption.styles
      }))
    }] : [];
    const defaultFontOptions = [{
      options: defaultFonts
    }];
    return [...parsedCustomFontOptions, ...defaultFontOptions, ...parsedFontOptions, ...webFontOptions];
  }, [formattedBrandingFonts, customFonts, defaultFonts, enableParsedFonts, parsedFonts, parsedFontText, webfonts, isUngatedForBrandingFonts]);
  return /*#__PURE__*/_jsx(GroupedFontSelectBody, Object.assign({}, rest, {
    error: isUnsupportedFont,
    fontOptions: options,
    value: value
  }));
}
GroupedFontSelect.propTypes = {
  customFonts: PropTypes.array,
  defaultFonts: PropTypes.array.isRequired,
  enableParsedFonts: PropTypes.bool,
  fetchBrandingCustomFonts: PropTypes.func,
  addBrandingCustomFonts: PropTypes.func,
  isUngatedForBrandingFonts: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  parsedFonts: PropTypes.array,
  parsedFontText: PropTypes.shape({
    help: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired
  }),
  unsupportedFonts: PropTypes.array,
  value: PropTypes.string,
  webfonts: PropTypes.array,
  StyledUITextToolbarSelectComponent: PropTypes.elementType,
  VariantButtonComponent: PropTypes.elementType,
  VariantOptionComponent: PropTypes.elementType,
  VariantSelectComponent: PropTypes.elementType
};
GroupedFontSelect.defaultProps = {
  customFonts: [],
  enableParsedFonts: false,
  parsedFonts: [],
  unsupportedFonts: [],
  webfonts: [],
  StyledUITextToolbarSelectComponent: UISelect,
  VariantButtonComponent: UIButton,
  VariantSelectComponent: UISelect
};
export default GroupedFontSelect;